// src/components/ui/MonthPicker.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const CalendarIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="3" ry="3" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ChevronLeftIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronDownIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CloseIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const pad2 = (n) => String(n).padStart(2, '0');

// Stable helper — no closure over `now`
const parseValueStatic = (val) => {
  if (!val) return null;
  const [y, m] = String(val).split('-').map(Number);
  if (!y || !m) return null;
  return { year: y, month: m };
};

const MonthPicker = ({
  value,
  onChange,
  minYear = 2023,
  maxDate = new Date(),
  locale = 'he',
  className = '',
  disabled = false,
  showLessonCount = false,
  lessonCount = 0,
}) => {
  const now = useRef(new Date()).current; // stable reference, won't re-create
  const maxYear  = maxDate.getFullYear();
  const maxMonth = maxDate.getMonth() + 1;
  const dir = locale === 'he' ? 'rtl' : 'ltr';

  const months = useMemo(() => {
    if (locale === 'he') {
      return [
        { value: 1,  name: 'ינואר',   short: 'ינו' },
        { value: 2,  name: 'פברואר',  short: 'פבר' },
        { value: 3,  name: 'מרץ',     short: 'מרץ' },
        { value: 4,  name: 'אפריל',   short: 'אפר' },
        { value: 5,  name: 'מאי',     short: 'מאי' },
        { value: 6,  name: 'יוני',    short: 'יונ' },
        { value: 7,  name: 'יולי',    short: 'יול' },
        { value: 8,  name: 'אוגוסט',  short: 'אוג' },
        { value: 9,  name: 'ספטמבר',  short: 'ספט' },
        { value: 10, name: 'אוקטובר', short: 'אוק' },
        { value: 11, name: 'נובמבר',  short: 'נוב' },
        { value: 12, name: 'דצמבר',   short: 'דצמ' },
      ];
    }
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      name:  new Date(2000, i).toLocaleString('en', { month: 'long' }),
      short: new Date(2000, i).toLocaleString('en', { month: 'short' }),
    }));
  }, [locale]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = maxYear; y >= minYear; y--) arr.push(y);
    return arr;
  }, [minYear, maxYear]);

  const isFutureMonth = useCallback((y, m) => {
    if (y > maxYear) return true;
    if (y === maxYear && m > maxMonth) return true;
    return false;
  }, [maxYear, maxMonth]);

  const fallbackYear  = now.getFullYear();
  const fallbackMonth = now.getMonth() + 1;

  const initial = useMemo(() => {
    const p = parseValueStatic(value);
    return p ?? { year: fallbackYear, month: fallbackMonth };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only for mount

  const [selectedYear,  setSelectedYear]  = useState(initial.year);
  const [selectedMonth, setSelectedMonth] = useState(initial.month);
  const [open,          setOpen]          = useState(false);
  const [panelYear,     setPanelYear]     = useState(initial.year);
  const wrapRef    = useRef(null);
  const dropRef    = useRef(null);

  // Sync when `value` prop changes externally
  useEffect(() => {
    const p = parseValueStatic(value);
    if (!p) return;
    setSelectedYear(p.year);
    setSelectedMonth(p.month);
    setPanelYear(p.year);
  }, [value]);

  // Notify parent only when internal selection actually changes
  useEffect(() => {
    const v = `${selectedYear}-${pad2(selectedMonth)}`;
    if (onChange && v !== value) onChange(v);
  // `onChange` intentionally omitted — callers rarely memoize it and including
  // it causes double-fire on mount. Parent drives truth via `value` prop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const monthName = months.find((m) => m.value === selectedMonth)?.name ?? '';
  const display   = `${monthName} ${selectedYear}`;

  const canGoPrev = !(selectedYear === minYear && selectedMonth === 1);
  const nextM     = selectedMonth === 12 ? 1 : selectedMonth + 1;
  const nextY     = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
  const canGoNext = !isFutureMonth(nextY, nextM);

  const changeMonth = (direction) => {
    let m = selectedMonth + direction;
    let y = selectedYear;
    if (m > 12) { m = 1;  y += 1; }
    else if (m < 1) { m = 12; y -= 1; }
    if (y < minYear) return;
    if (isFutureMonth(y, m)) return;
    setSelectedMonth(m);
    setSelectedYear(y);
    setPanelYear(y);
  };

  const close  = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown',   onKey);
    };
  }, [open, close]);

  // ── Viewport-aware dropdown positioning ──────────────────────────────────
  // After the dropdown renders, nudge it so it never clips the screen edge.
  useEffect(() => {
    if (!open || !dropRef.current) return;
    const el   = dropRef.current;
    const rect = el.getBoundingClientRect();
    const vw   = window.innerWidth;

    // Reset any previous nudge so we measure from natural position
    el.style.transform = '';

    if (rect.right > vw - 8) {
      const overflow = rect.right - (vw - 8);
      el.style.transform = dir === 'rtl'
        ? `translateX(${overflow}px)`   // push inward (positive = right in RTL)
        : `translateX(-${overflow}px)`;
    }
    if (rect.left < 8) {
      const overflow = 8 - rect.left;
      el.style.transform = dir === 'rtl'
        ? `translateX(-${overflow}px)`
        : `translateX(${overflow}px)`;
    }
  }, [open, dir]);

  const pickMonth = (m) => {
    if (disabled) return;
    if (isFutureMonth(panelYear, m)) return;
    setSelectedYear(panelYear);
    setSelectedMonth(m);
    close();
  };

  const quickToday = () => {
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
    setPanelYear(now.getFullYear());
    close();
  };

  const quickLastMonth = () => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setSelectedYear(d.getFullYear());
    setSelectedMonth(d.getMonth() + 1);
    setPanelYear(d.getFullYear());
    close();
  };

  if (disabled) {
    return (
      <div className={`opacity-50 cursor-not-allowed ${className}`} dir={dir}>
        <div className="px-4 py-2.5 bg-gray-100 rounded-xl text-gray-500 font-medium text-sm">
          {display}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center max-w-full ${className}`}
      dir={dir}
      ref={wrapRef}
    >
      {showLessonCount && lessonCount > 0 && (
        <div className="mr-2 px-2.5 py-1 bg-purple-50 rounded-lg border border-purple-200 flex-shrink-0">
          <span className="text-xs font-semibold text-purple-700">{lessonCount}</span>
        </div>
      )}

      <div className="inline-flex items-center bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow min-w-0">

        {/* ── Prev ── */}
        <button
          onClick={() => changeMonth(-1)}
          disabled={!canGoPrev}
          type="button"
          aria-label={locale === 'he' ? 'חודש קודם' : 'Previous month'}
          className={`flex-shrink-0 p-2 sm:p-2.5 hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
            locale === 'he' ? 'rounded-r-2xl' : 'rounded-l-2xl'
          }`}
        >
          {locale === 'he'
            ? <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            : <ChevronLeftIcon  className="w-4 h-4 text-gray-600" />}
        </button>

        {/* ── Center trigger ── */}
        <div className="relative min-w-0">
          <button
            onClick={toggle}
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            className={`px-2.5 sm:px-4 py-2 min-w-[120px] sm:min-w-[160px] hover:bg-gray-50 transition-colors border-x border-gray-200 ${
              open ? 'bg-gray-50' : ''
            }`}
          >
            <div className="flex items-center justify-between gap-1.5 sm:gap-3">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <CalendarIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-900 text-sm truncate">{display}</span>
              </div>
              <ChevronDownIcon
                className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>

          {/* ── Dropdown ── */}
          {open && (
            <div
              ref={dropRef}
              role="dialog"
              aria-label={locale === 'he' ? 'בחר חודש' : 'Select month'}
              // Width: fills screen on very small viewports, capped at 320px on larger ones.
              // Horizontal anchor: right-aligned in RTL, left-aligned in LTR.
              // `transform` may be nudged at runtime by the viewport-overflow effect.
              className={`absolute z-50 mt-2 w-[min(320px,90vw)] bg-white rounded-xl border border-gray-200 shadow-xl ${
                dir === 'rtl' ? 'right-0' : 'left-0'
              }`}
            >
              {/* Caret */}
              <div
                className={`absolute -top-2 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45 ${
                  dir === 'rtl' ? 'right-4' : 'left-4'
                }`}
              />

              {/* Header */}
              <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {locale === 'he' ? 'בחר חודש' : 'Select Month'}
                </h3>
                <button
                  onClick={close}
                  type="button"
                  aria-label="סגור"
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <CloseIcon className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <div className="p-3 sm:p-4">
                {/* Year selector */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {locale === 'he' ? 'שנה' : 'Year'}
                  </label>
                  {/* Scrollable row — never wraps, always horizontally scrollable */}
                  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    {years.map((y) => (
                      <button
                        key={y}
                        onClick={() => setPanelYear(y)}
                        type="button"
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                          panelYear === y
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Month grid — 4 columns keeps labels readable at narrow widths */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {locale === 'he' ? 'חודש' : 'Month'}
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {months.map((m) => {
                      const isSelected = selectedYear === panelYear && selectedMonth === m.value;
                      const isFuture   = isFutureMonth(panelYear, m.value);
                      const isCurrent  = panelYear === now.getFullYear() && m.value === now.getMonth() + 1;
                      return (
                        <button
                          key={m.value}
                          onClick={() => pickMonth(m.value)}
                          disabled={isFuture}
                          type="button"
                          className={`relative py-2 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : isFuture
                              ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                              : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {/* Use short names in the 4-col grid so they always fit */}
                          {m.short}
                          {isCurrent && !isSelected && (
                            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quick actions */}
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={quickToday}
                    type="button"
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors"
                  >
                    {locale === 'he' ? 'החודש' : 'This Month'}
                  </button>
                  <button
                    onClick={quickLastMonth}
                    type="button"
                    className="flex-1 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors"
                  >
                    {locale === 'he' ? 'חודש שעבר' : 'Last Month'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Next ── */}
        <button
          onClick={() => changeMonth(1)}
          disabled={!canGoNext}
          type="button"
          aria-label={locale === 'he' ? 'חודש הבא' : 'Next month'}
          className={`flex-shrink-0 p-2 sm:p-2.5 hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
            locale === 'he' ? 'rounded-l-2xl' : 'rounded-r-2xl'
          }`}
        >
          {locale === 'he'
            ? <ChevronLeftIcon  className="w-4 h-4 text-gray-600" />
            : <ChevronRightIcon className="w-4 h-4 text-gray-600" />}
        </button>
      </div>
    </div>
  );
};

export default MonthPicker;